/**
 * Der Inline-Editor der Bestellliste - `orderListEditorCell` (index.html:7657) und
 * `patchOrderListField` (:7690) als reine Funktionen, ohne React und ohne Netz.
 *
 * ================= Warum dieses Modul ueberhaupt existiert =================
 *
 * Der Bestand kann sich den Editor billig leisten: `patchOrderListField` mutiert das Objekt im
 * Speicher und ruft `save()`. Die SPA schreibt stattdessen gegen den Server, und dabei gehen die
 * 13 Spalten NICHT alle denselben Weg:
 *
 *   `due`, `etdPi`, `etd`, `eta`, `custom_*`                 -> Feldgruppe `extras`
 *   `value`                                                  -> Feldgruppe `costs`
 *   `status`                                                 -> EIGENER Endpunkt (Verlaufseintrag!)
 *   `name`, `invoiceNo`, `supplier`, `booked`, `dsvShipmentNo`, `payment`, `zoll` -> gar nicht, siehe `NICHT_BEARBEITBAR`
 *
 * Diese Zuordnung braucht die ANZEIGE (welche Zelle bietet ueberhaupt einen Editor an?) und der
 * SCHREIBWEG (wohin geht der Wert?). Stuende sie zweimal da - einmal in `Board/Bestellzeile.tsx`,
 * einmal in `components/App/App.tsx` -, waere die naechste Spalte die, bei der die beiden
 * auseinanderlaufen: die Zelle boete einen Editor an, dessen Wert nirgends ankommt. Genau diese
 * Klasse ("sichtbares Bedienelement ohne Wirkung") hat dieses Projekt schon einmal Monate gekostet,
 * siehe die Karte `unverdrahtet` in `test/reference/bundle.test.ts`. Deshalb EINE Tabelle, von
 * beiden Seiten gelesen.
 *
 * ================= Die sieben Spalten ohne Schreibweg =================
 *
 * `name` ist die Bestellnummer (`orderNo`). Sie steht in `KEIN_FELDGRUPPEN_WEG`
 * (`api/bestellungen.ts`): JTL vergibt `cEigeneBestellnummer` beim Speichern, das Tool liest sie nur
 * zurueck. Ein Editor darauf erzeugte bei jedem Uebernehmen einen `FeldOhneGruppeFehler` samt Toast.
 *
 * `invoiceNo` ist die Rechnungsnummer (`invoiceNo`). JTL vergibt sie beim Speichern; der Hub liest
 * sie nur zurueck. Ein Editor darauf haette ebenfalls keinen Schreibweg.
 *
 * `supplier` hat ueberhaupt keine Spalte in `FELDGRUPPEN_SPALTEN` - der Lieferant haengt als
 * `kLieferant` am Kopf und gehoert JTL. Zusaetzlich ist ein Wechsel im Bestand mehr als ein Feld:
 * `setSupplier` (index.html:9906) uebernimmt Zahlungsziel und Ratenplan aus den Stammdaten und legt
 * Zahlungen an - das ist der eigene, bis heute unverdrahtete Kanal `onLieferant`
 * (`Drawer/MainTab/typen.ts`), kein Textfeld in einer Tabellenzelle.
 *
 * `booked` ("Ware eingebucht") war frueher ein eigenes Hub-Feld (`bookedDate`/`deliveryBooked`,
 * Feldgruppe `extras`) und damit editierbar. Seit dessen Entfernung
 * (`backend/migrations/026_order_extras_booked_entfernen.sql`) leitet `lib/orderList.ts#vollstaendigGeliefert`
 * die Spalte allein aus den JTL-Positionsmengen (`OrderItem.anzahlOffen`) ab - ein Editor haette
 * keinen Schreibweg mehr, an den er andocken koennte.
 *
 * ABWEICHUNG VOM BESTAND, benannt: dort tragen alle drei Zellen (`name`/`supplier`/`booked`) einen
 * Editor, der schreibt (`booked` frueher jedenfalls). Hier tragen sie weiterhin die anklickbare
 * Huelle (`Bearbeitbar`, DOM also unveraendert - die Baselines bleiben gruen), aber der Klick liefert
 * die Begruendung aus `NICHT_BEARBEITBAR` statt eines Editors, dessen Uebernahme scheitern MUSS.
 * Lieber ein Satz, der erklaert, als ein Feld, das luegt.
 *
 * `dsvShipmentNo` ("Trackingnummer", Auftrag "Spalte Trackingnummer im Bestellboard") hat KEIN
 * Vorbild im Bestand - die Spalte gab es dort nicht, sie liest `shipments.dsv_shipment_no` ueber
 * `order_shipments` (`backend/src/domaene/orders.ts`, BESTELLUNG_MIT_EXTRAS). Sie traegt trotzdem
 * denselben Mechanismus wie `booked`: die Sendungsnummer lebt in `shipments`, nicht in
 * `orders`/`order_extras` - es gibt keinen Feldgruppen-Schreibweg, an den ein Editor andocken
 * koennte, deshalb `NICHT_BEARBEITBAR` statt eines eigenen REGELN-Eintrags.
 *
 * `payment` ("Zahlung anstoßen", Auftrag "Zahlung anstoßen Knopf + Einfügung ... in der Übersicht",
 * Asana Task 1218171668075282) ist der einzige der sieben, der - anders als `booked`/`dsvShipmentNo`
 * - DURCHAUS einen Feldgruppen-Schreibweg hat (`payment-request`,
 * `order_extras.payment_initiated_at`/`payment_initiated_by`, `PATCH /:id/payment-request`). Die
 * Board-Zelle zeigt trotzdem nur den abgeleiteten Zustand (gesetzt/nicht gesetzt), wie bei `booked`:
 * geschrieben wird ausschliesslich ueber den eigenen Knopf "Zahlung anstoßen" im Zahlung-Tab
 * (`Drawer/PaymentTab/Freigabe.tsx`), der Zeitpunkt UND Nutzer atomar zusammen setzt und damit
 * bewusst protokolliert, wer wann ausgeloest hat. Ein freies Textfeld in der Board-Zelle koennte das
 * nicht abbilden (welchen Wert sollte ein Tippen hier ueberhaupt erzeugen?) und wuerde die
 * Protokollpflicht umgehen - deshalb `NICHT_BEARBEITBAR` statt eines REGELN-Eintrags, wie bei den
 * anderen schreibgeschuetzten Spalten.
 *
 * `zoll` ("Zollinstruktionen anstoßen", Spec Zollinstruktionen im Hub) ist die siebte Spalte ohne
 * Editor: die Zelle zeigt nur, ob der Task „Zollinstruktionen an Spedition senden (Outlook)" erledigt
 * ist (`lib/zollinstruktionen.ts`). Es gibt kein eigenes Feld, das ein Editor setzen koennte: der Zustand
 * ergibt sich allein aus dem Task. Der Knopf im Reiter Spedition erzeugt die Datei und hakt den Task in
 * einem Schritt ab; ein im Aufgaben-Reiter von Hand abgehakter Task faerbt die Zelle genauso, und
 * Zurueckhaken leert sie wieder (der Hub hakt nie automatisch zurueck). Ohne den Eintrag in
 * `NICHT_BEARBEITBAR` fiele die Zelle auf den Freitext-Editor fuer eigene Listenfelder zurueck und
 * schriebe `customFields.zoll`.
 *
 * ================= ETD/ETA: bedienbar, aber nicht immer =================
 *
 * Sobald eine DSV-Sendung verknuepft ist, fuehrt SIE `etd` und `eta` (Spec,
 * Verhaltensaenderung 5 - dieselbe Regel, die `Drawer/MainTab/Termine.tsx` mit `disabled` plus
 * Kennzeichnung „DSV-geführt" abbildet). Das Backend setzt sie ebenfalls durch
 * (`istDsvGefuehrt`/`DsvGefuehrtesFeldError`, `backend/src/domaene/extras.ts`) - ein Editor hier
 * liefe also in einen Serverfehler. `orderListEditorRegel` gibt fuer die beiden deshalb dieselbe
 * Sperre zurueck wie fuer `name`/`supplier`, sobald eine Sendung haengt.
 *
 * `etdPi` ist davon NICHT betroffen: es ist die Zusage aus der Proforma, keine Transportmeldung
 * (siehe Kopfkommentar `Drawer/ShipTab/Transport.tsx`, "WELCHES ETD?" - der Abschnitt zog beim
 * Auftrag „Reiter Spedition" von `Termine.tsx` dorthin um, C4 einer Pruefung, 2026-08-21).
 */
import { estimatedBuchenDateFromEta, etaBookingOffsetDays, orderListEditableAmount, parseOrderListAmount } from "./format.ts";
import { orderTotal } from "./payments.ts";
import { orderListDue } from "./status.ts";
import type { StatusDefinitionen } from "./status.ts";
import type { Order, Sendung } from "./types.ts";

/**
 * index.html:7463 - das Praefix jeder vom Benutzer angelegten Listenspalte, und es ist TRAGEND:
 * `setOrderListCustomValue` (:7585) nimmt NUR Schluessel mit diesem Praefix an. Eine Spalte ohne es
 * wird angezeigt und nie gespeichert (siehe `orderListSchreibweg`, Rueckfallzweig).
 *
 * Beheimatet seit dem 2026-08-15 hier statt in `Board/ListenEditor.tsx` (das es von hier
 * re-exportiert): beide Seiten brauchen ihn - der Spalteneditor beim ANLEGEN, der Zelleneditor beim
 * SCHREIBEN -, und `lib/` darf nicht auf `components/` zeigen. NICHT in `config/board.ts`, obwohl er
 * dort thematisch hingehoerte: `scripts/gen-herkunft.ts` prueft jedes Symbol der neunzehn
 * portierten `lib/`- und `config/`-Module gegen einen gleichnamigen Bezeichner in `index.html` - und
 * einen solchen gibt es nicht, der Bestand schreibt den Wert in `addOrderListColumn` aus.
 */
export const EIGENE_SPALTE_PRAEFIX = "custom_";

/**
 * Die Bauart des Bedienelements - `orderListEditorCell` unterscheidet vier, an den CSS-Klassen
 * ablesbar (`order-list-inline-name`/`-date`/`-supplier`/`-status`/`-money`).
 *
 * `uebernahme` ist der Unterschied, den man leicht uebersieht: Textfelder uebernehmen bei `blur`
 * (`commitOnBlur`), Datums- und Auswahlfelder bei `change` UND verwerfen bei `blur`
 * (`commitOnChange` = `onchange="commit…" onblur="cancel…"`). Ein Datumsfeld, das erst bei `blur`
 * uebernaehme, verloere den Wert, sobald der Kalender geschlossen wird.
 */
export type EditorArt = "text" | "datum" | "betrag" | "status";

export interface EditorRegel {
  art: EditorArt;
  /** Der Kern des `aria-label` - im Bestand „<Feld> für <Bestellung>". */
  label: string;
  /** Die Klassen des Bestands am Bedienelement, woertlich. */
  klasse: string;
  uebernahme: "blur" | "change";
}

/**
 * index.html:7657-7684, je Zweig eine Zeile. `name`, `supplier`, `booked`, `dsvShipmentNo`,
 * `payment`, `zoll` und `invoiceNo` fehlen ABSICHTLICH - siehe `NICHT_BEARBEITBAR` und den Kopfkommentar.
 */
const REGELN: ReadonlyMap<string, EditorRegel> = new Map<string, EditorRegel>([
  ["due", { art: "datum", label: "Fälligkeitsdatum", klasse: "order-list-inline-control order-list-inline-date mono", uebernahme: "change" }],
  ["status", { art: "status", label: "Status", klasse: "order-list-inline-control order-list-inline-status", uebernahme: "change" }],
  ["value", { art: "betrag", label: "Bestellwert", klasse: "order-list-inline-control order-list-inline-money mono", uebernahme: "blur" }],
  ["etdPi", { art: "datum", label: "PI-ETD", klasse: "order-list-inline-control order-list-inline-date mono", uebernahme: "change" }],
  ["etd", { art: "datum", label: "ETD", klasse: "order-list-inline-control order-list-inline-date mono", uebernahme: "change" }],
  ["eta", { art: "datum", label: "ETA", klasse: "order-list-inline-control order-list-inline-date mono", uebernahme: "change" }],
]);

/** Der Editor jeder eigenen Spalte (`custom_…`) - index.html:7684, der Rueckfallzweig. */
const REGEL_EIGENE_SPALTE: EditorRegel = {
  art: "text",
  label: "Eigenes Listenfeld",
  klasse: "order-list-inline-control",
  uebernahme: "blur",
};

/**
 * Das `aria-label` des Bedienelements - und es ist NICHT einheitlich: sieben Zweige schreiben
 * „<Feld> für <Bestellung>" (index.html:7663-7683), der Rueckfallzweig fuer eigene Spalten dagegen
 * „Eigenes Listenfeld bearbeiten" (:7684) ohne jede Bestellung. Woertlich uebernommen.
 *
 * Die Bestellung heisst hier `orderNo || name || "Bestellung"` - dieselbe Rueckfallkette wie im
 * Kontextmenue und BEWUSST eine andere als in der Namenszelle der Liste, die auf „Ohne
 * Bestellnummer" faellt (siehe `Board/Bestellzeile.tsx`, Eigenheit 5).
 */
export function orderListEditorAria(regel: EditorRegel, o: Order): string {
  if (regel === REGEL_EIGENE_SPALTE) return `${regel.label} bearbeiten`;
  return `${regel.label} für ${(o.orderNo as string) || (o.name as string) || "Bestellung"}`;
}

/**
 * Die Spalten OHNE Schreibweg, mit dem Satz, den der Nutzer beim Klick zu sehen bekommt. Kein
 * Fachjargon: „Feldgruppe" und „kLieferant" helfen an dieser Stelle niemandem.
 */
export const NICHT_BEARBEITBAR: ReadonlyMap<string, string> = new Map([
  ["name", "Die Bestellnummer vergibt JTL beim Speichern - der Hub liest sie nur zurück."],
  ["invoiceNo", "Die Rechnungsnummer vergibt JTL beim Speichern - der Hub liest sie nur zurück."],
  [
    "supplier",
    "Der Lieferant lässt sich nur in der Bestellung selbst wechseln: der Wechsel zieht Zahlungsziel und Ratenplan nach.",
  ],
  [
    "booked",
    "„Ware eingebucht“ ergibt sich aus den offenen JTL-Positionsmengen und lässt sich nicht direkt setzen.",
  ],
  [
    "dsvShipmentNo",
    "Die Trackingnummer kommt aus der verknüpften DSV-Sendung (shipments.dsv_shipment_no) und lässt sich hier nicht setzen.",
  ],
  [
    "payment",
    "„Zahlung anstoßen“ ergibt sich aus dem Zeitpunkt der angestoßenen Zahlung und lässt sich hier nicht setzen.",
  ],
  [
    "zoll",
    "„Zollinstruktionen anstoßen“ ergibt sich aus dem erledigten Task „Zollinstruktionen an Spedition senden (Outlook)“ und lässt sich hier nicht setzen.",
  ],
]);

/** Der Satz für ein DSV-geführtes Feld - dieselbe Aussage wie `Termine.tsx`s Kennzeichnung. */
export const DSV_GESPERRT_MELDUNG =
  "ETD und ETA kommen aus der verknüpften DSV-Sendung und werden nur über Transportereignisse aktualisiert.";

/** Die Meldung eines unbrauchbaren Betrags - index.html:7707, woertlich. */
export const BETRAG_UNGUELTIG_MELDUNG = "Bitte einen gültigen Bestellwert eingeben";

/** Die vier Felder, die eine verknuepfte Sendung fuehrt - hier nur die zwei aus der Bestellliste. */
const DSV_GEFUEHRT = new Set(["etd", "eta"]);

export type EditorEntscheidung =
  | { art: "editor"; regel: EditorRegel }
  | { art: "gesperrt"; grund: string };

/**
 * Die Sendungsnummer der Sammelsendung, die den Status dieser Bestellung zentral fuehrt: ein Mitglied
 * ist, wer `shipmentParent` mit mindestens zwei Mitgliedern traegt (dieselbe Regel wie
 * `sperrendeSammelsendung` im Drawer und `istSammelsendungsMitglied` im Backend), `null` sonst.
 */
function statusfuehrendeSammelsendung(o: Order | null | undefined): string | null {
  const parent = o?.shipmentParent;
  return parent && parent.memberCount >= 2 ? parent.dsvShipmentNo : null;
}

/** Der Satz, wenn der Status eines Sammelsendungs-Mitglieds direkt geaendert werden soll (Spec 29.09.). */
export function sammelsendungStatusGesperrtMeldung(dsvShipmentNo: string): string {
  return `Der Status wird zentral in der Sammelsendung ${dsvShipmentNo} gepflegt - bitte dort ändern.`;
}

/**
 * Bietet diese Zelle einen Editor an - und wenn nicht, warum nicht?
 *
 * `sendung` ist die zur Bestellung verknuepfte Sendung (`GET /api/orders/:orderId/shipment`); `null`
 * heisst "keine verknuepfte Sendung" und laesst `etd`/`eta` frei. Die Bestellliste (`BoardSeite.tsx`)
 * hat dafuer seit dem Auftrag „Sendungsabruf beim App-Start entfernen" (2026-08-22) KEINE Quelle
 * mehr und reicht deshalb immer `null` - der Zweig bleibt trotzdem stehen, weil er die Anzeigeseite
 * einer Backend-Regel ist (`istDsvGefuehrt`/`DsvGefuehrtesFeldError`,
 * `backend/src/domaene/extras.ts`) und der Drawer (`ShipTab/Transport.tsx`) die echte Sendung
 * weiterhin aufloest.
 */
export function orderListEditorRegel(
  key: string,
  sendung: Sendung | null,
  bestellung?: Order | null,
): EditorEntscheidung {
  const gesperrt = NICHT_BEARBEITBAR.get(key);
  if (gesperrt) return { art: "gesperrt", grund: gesperrt };
  // Der Status eines Sammelsendungs-Mitglieds gehoert der Sammelsendung; das Backend lehnt den
  // direkten Wechsel mit 409 ab, hier kommt statt des Editors die Begruendung.
  const sammelsendung = key === "status" ? statusfuehrendeSammelsendung(bestellung) : null;
  if (sammelsendung !== null) return { art: "gesperrt", grund: sammelsendungStatusGesperrtMeldung(sammelsendung) };
  if (sendung !== null && DSV_GEFUEHRT.has(key)) {
    return { art: "gesperrt", grund: DSV_GESPERRT_MELDUNG };
  }
  const regel = REGELN.get(key);
  if (regel) return { art: "editor", regel };
  // Der Rueckfallzweig des Bestands (:7684) faengt JEDEN unbekannten Schluessel als eigenes Feld ab -
  // auch die zehn aus `REMOVED_ORDER_LIST_KEYS`. Uebernommen, siehe `lib/orderList.ts`, Eigenheit 1.
  return { art: "editor", regel: REGEL_EIGENE_SPALTE };
}

/**
 * Der Wert, mit dem der Editor aufgeht - index.html:7659-7684, je Zweig das `value=`-Stueck.
 *
 * `booked` fehlt ABSICHTLICH: die Spalte ist seit der Ableitung aus den JTL-Positionsmengen
 * (`lib/orderList.ts#vollstaendigGeliefert`) nicht mehr editierbar, siehe `NICHT_BEARBEITBAR`.
 */
export function orderListEditorWert(o: Order, key: string): string {
  switch (key) {
    case "due":
      return orderListDue(o);
    case "status":
      return (o.status as string) || "";
    case "value":
      return orderListEditableAmount(orderTotal(o));
    case "etdPi":
    case "etd":
    case "eta":
      return (o[key] as string) || "";
    default:
      return String((o.customFields as Record<string, unknown>)?.[key] || "");
  }
}

/**
 * Was mit dem uebernommenen Wert geschehen soll - der Ersatz fuer `patchOrderListField` (:7690),
 * das im Bestand mutiert und speichert. Hier faellt nur die ENTSCHEIDUNG; das Schreiben gehoert dem
 * Aufrufer (`components/App/App.tsx`), wie ueberall in diesem Baum.
 */
export type Schreibweg =
  /** Ueber `bestellungAendern` - die Feldgruppe ermittelt `gruppeUndFelder` aus den Feldnamen. */
  | { art: "felder"; aenderung: Record<string, unknown> }
  /** Ueber `boardStatusAendern` - eigener Endpunkt, weil ein Verlaufseintrag dazugehoert. */
  | { art: "status"; status: string }
  /** Nichts zu tun (unveraendert oder unbekannter Status) - der Bestand zeichnet nur neu. */
  | { art: "nichts" }
  /** Laut abbrechen, mit Meldung an den Nutzer. */
  | { art: "fehler"; meldung: string };

/**
 * index.html:7690-7724, Zweig fuer Zweig - mit den drei Stellen, an denen die REST-Fassung
 * zwangslaeufig anders aussieht als die Speichermutation des Bestands:
 *
 * 1. `delete order.orderValueOverride` (leerer Betrag) wird zu `{ orderValueOverride: null }` -
 *    ueber HTTP gibt es kein "loesche den Schluessel", die Spalte `costs.order_value_override` ist
 *    NULL-faehig und `updateVersioned` setzt sie damit zurueck. Fachlich dasselbe: `orderTotal`
 *    prueft `hasOwnProperty`, und nach dem Nachladen fuehrt der Server den Schluessel gar nicht mehr.
 * 2. `setOrderEta` (:4793) zieht `estimatedDeliveryBuchen` mit - hier als zweites Feld IM SELBEN
 *    Patch, weil beide in `extras` liegen und zwei Aufrufe die zweite `rowVersion` verloeren
 *    (`MehrereGruppenFehler`-Kommentar in `api/bestellungen.ts`). Dieselbe Form wie
 *    `Termine.tsx#etaSetzen`.
 * 3. `setOrderListCustomValue` (:7583) mutiert `order.customFields` an Ort und Stelle; hier wird das
 *    GANZE Objekt neu gebildet und als eine Spalte (`custom_fields`) geschickt.
 *
 * Woertlich uebernommen bleibt der Rest, auch wo es ueberrascht:
 * - Der Status prueft `!STATUS_MAP[value] || order.status === value` und tut dann NICHTS.
 * - `setOrderListCustomValue` nimmt NUR Schluessel mit dem Praefix `custom_` an; eine eigene Spalte
 *   mit anderem Schluessel wird angezeigt (`orderListCell`s Rueckfallzweig liest `customFields[key]`
 *   ohne Praefixpruefung), aber NIE gespeichert. `ListenEditor.tsx` vergibt nur Schluessel mit
 *   Praefix, der Fall ist im lebenden Pfad also unerreichbar - er ist trotzdem nachgebaut.
 * - Ein leerer Text LOESCHT das eigene Feld (`String(value).trim()`), er speichert keinen Leerwert.
 */
export function orderListSchreibweg(
  o: Order,
  key: string,
  wert: string,
  definitionen: StatusDefinitionen,
): Schreibweg {
  switch (key) {
    case "due":
      return { art: "felder", aenderung: { listDueDate: wert || "" } };
    case "status": {
      // Auch ein spaet eintreffender Wechsel (Sperre erst waehrend der Bearbeitung entstanden) geht nie raus.
      const sammelsendung = statusfuehrendeSammelsendung(o);
      if (sammelsendung !== null) return { art: "fehler", meldung: sammelsendungStatusGesperrtMeldung(sammelsendung) };
      // :7699 - ein unbekannter Status und der unveraenderte Status fallen in denselben Zweig.
      if (!definitionen.STATUS_MAP[wert] || o.status === wert) return { art: "nichts" };
      return { art: "status", status: wert };
    }
    case "value": {
      const betrag = parseOrderListAmount(wert);
      if (betrag === null) return { art: "felder", aenderung: { orderValueOverride: null } };
      if (Number.isNaN(betrag)) return { art: "fehler", meldung: BETRAG_UNGUELTIG_MELDUNG };
      return { art: "felder", aenderung: { orderValueOverride: betrag } };
    }
    case "eta":
      return {
        art: "felder",
        aenderung: {
          eta: wert || "",
          estimatedDeliveryBuchen: estimatedBuchenDateFromEta(wert || "", etaBookingOffsetDays(o)),
        },
      };
    case "etdPi":
    case "etd":
      return { art: "felder", aenderung: { [key]: wert || "" } };
    default: {
      if (!key.startsWith(EIGENE_SPALTE_PRAEFIX)) return { art: "nichts" };
      const felder = { ...((o.customFields as Record<string, unknown>) || {}) };
      if (String(wert).trim()) felder[key] = wert;
      else delete felder[key];
      return { art: "felder", aenderung: { customFields: felder } };
    }
  }
}
